import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  PERMISSIONS,
  formatApiError,
  useActiveAlarms,
  useCardDialogOpenSignal,
  usePermissions,
  useReaderPrecondition,
  useRooms,
  useSiteConfig,
  useTranslation,
  useUpdateOccupancy,
} from '@bms/common';
import type { Room } from '@bms/common';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateTimeInput } from '@bms/common/ui';
import { Label } from '@/components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { KeyEncodeStep, ReaderStatusLine, type EncodedKey } from './KeyEncodeStep';
import { checkoutAfterNights, houseCheckoutTime, isSellable, useSiteDates } from './deskRules';
import { isSnoozedDeviation } from '@/lib/grms/alarms';

export interface CheckInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The room being checked into. Omitted for a walk-in from a tapped card: step 1
   * then offers the rooms that are ready to sell, and the card still on the reader
   * becomes key 1 in step 2.
   */
  roomCode?: string;
  /** The room's display label, when the host knows it (the room dialog does). */
  roomLabel?: string | null;
  /** Called once the stay is written, with the room it was written to. */
  onCheckedIn?: (roomCode: string) => void;
}

type Step = 'guest' | 'keys' | 'done';

const KEY_COUNTS = [1, 2, 3, 4] as const;
const MAX_NIGHTS = 365;

/** `datetime-local` value ↔ ISO, in the browser's zone, as the stay editor uses. */
function isoToLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

/**
 * Guided check-in (audit P3; UX-10, UX-12, Idea 3): one modal, three steps.
 *
 * 1. **Guest** — name, nights (checkout = site-local date + the house checkout
 *    time, with the raw picker behind "change time"), number of keys; the reader
 *    line is already visible.
 * 2. **Keys** — `KeyEncodeStep`.
 * 3. **Done** — guest in, keys written, checkout.
 *
 * The failure rule: the occupancy is written first, when step 1 is confirmed. A
 * key that then fails to write leaves the room checked in and the step on its
 * error with Retry — nothing closes. Keys go through the standalone card route
 * (`POST /access/cards`), which the GRMS spec names for a check-in that carries
 * no encoded card (GRMS §11.2 Occupancy requests).
 */
export function CheckInDialog({ open, onOpenChange, roomCode, roomLabel: knownLabel, onCheckedIn }: CheckInDialogProps) {
  const { t } = useTranslation();
  const { dayTime } = useSiteDates();
  const walkIn = !roomCode;
  const canIssueKeys = usePermissions().has(PERMISSIONS.CARDS_WRITE);
  const updateOccupancy = useUpdateOccupancy();
  const site = useSiteConfig({ enabled: open });
  const { data: rooms = [] } = useRooms({ enabled: open });
  const { data: activeAlarms = [] } = useActiveAlarms();
  // The reader line is on screen from step 1; KeyEncodeStep reads its own.
  const precondition = useReaderPrecondition(open);

  const [step, setStep] = useState<Step>('guest');
  const [pickedRoom, setPickedRoom] = useState('');
  const [guestName, setGuestName] = useState('');
  const [nights, setNights] = useState('1');
  const [customCheckout, setCustomCheckout] = useState<string | null>(null);
  const [keyCount, setKeyCount] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [stayCheckout, setStayCheckout] = useState<string | null>(null);
  const [keys, setKeys] = useState<EncodedKey[]>([]);
  const [writing, setWriting] = useState(false);
  const [notReadyConfirmed, setNotReadyConfirmed] = useState(false);
  const wasOpen = useRef(false);

  useCardDialogOpenSignal(open);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setStep('guest');
      setPickedRoom('');
      setGuestName('');
      setNights('1');
      setCustomCheckout(null);
      setKeyCount(1);
      setError(null);
      setStayCheckout(null);
      setKeys([]);
      setWriting(false);
      setNotReadyConfirmed(false);
    }
    wasOpen.current = open;
  }, [open]);

  const timeZone = site.data?.timezone;
  const checkoutTime = houseCheckoutTime(site.data?.settings);
  const nightCount = Math.min(MAX_NIGHTS, Math.max(1, Number.parseInt(nights, 10) || 1));
  const computedCheckout = useMemo(
    () => checkoutAfterNights(new Date(), nightCount, checkoutTime, timeZone),
    [checkoutTime, nightCount, timeZone],
  );
  const checkout = customCheckout ?? computedCheckout;

  const readyRooms = useMemo(() => {
    const alarmed = new Set(
      activeAlarms
        .filter((alarm) => (alarm.status === 'active' || alarm.status === 'acknowledged') && !isSnoozedDeviation(alarm))
        .map((alarm) => alarm.roomCode),
    );
    return rooms
      .filter((room: Room) => isSellable(room, alarmed))
      .sort((a: Room, b: Room) => (a.roomLabel ?? a.roomCode).localeCompare(b.roomLabel ?? b.roomCode));
  }, [activeAlarms, rooms]);

  const targetRoom = roomCode ?? pickedRoom;
  const guest = guestName.trim() || null;
  // A room whose finish is not Ready has no working door yet: its keys open nothing. Commissioning
  // still checks such rooms in, so the desk warns and asks rather than refuses.
  const roomNotReady = rooms.find((room: Room) => room.roomCode === targetRoom)?.ready === false;

  const confirmGuest = async () => {
    if (!targetRoom) return;
    setError(null);
    try {
      await updateOccupancy.mutateAsync({
        roomCode: targetRoom,
        status: 'occupied',
        ...(guest ? { guestName: guest } : {}),
        expectedCheckout: checkout,
      });
    } catch (checkInError) {
      // The stay was refused: stay on step 1 with the reason, and raise the same
      // error toast the room dialog raises for every refused action.
      const reason = formatApiError(checkInError, t, { serverMessage: false });
      setError(reason);
      toast.error(t('checkIn.failed'), { id: 'check-in', description: reason });
      return;
    }
    setStayCheckout(checkout);
    onCheckedIn?.(targetRoom);
    setStep(canIssueKeys ? 'keys' : 'done');
  };

  const finishKeys = useCallback((written: EncodedKey[]) => {
    setKeys(written);
    setStep('done');
  }, []);

  // A write in flight is never abandoned by an outside click or Escape.
  const handleOpenChange = (next: boolean) => {
    if (!next && writing) return;
    onOpenChange(next);
  };

  const ownLabel = targetRoom ? (knownLabel ?? rooms.find((room) => room.roomCode === targetRoom)?.roomLabel) : undefined;
  const roomLabel = targetRoom ? (ownLabel ?? targetRoom) : '';
  // Code first, like the board: "soba-1 · qwe"; the label is left out when absent or equal to the code.
  const roomHeading = targetRoom ? (ownLabel && ownLabel !== targetRoom ? `${targetRoom} · ${ownLabel}` : targetRoom) : '';
  const stepIndex = step === 'guest' ? 1 : step === 'keys' ? 2 : 3;
  // The desk's day words: "1 night · out Thu 29 Oct 11:00" (F13).
  const checkoutText = dayTime(checkout);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {roomHeading ? t('checkIn.titleRoom', { room: roomHeading }) : t('checkIn.title')}
          </DialogTitle>
          <DialogDescription>
            {t('checkIn.stepOf', { step: stepIndex, total: 3, name: t(`checkIn.step.${step}`) })}
          </DialogDescription>
        </DialogHeader>

        {step === 'guest' && (
          <div className="grid gap-4 py-2">
            {walkIn && (
              <div className="grid gap-1.5">
                <Label htmlFor="checkin-room">{t('checkIn.room')}</Label>
                {readyRooms.length > 0 ? (
                  <select
                    id="checkin-room"
                    value={pickedRoom}
                    onChange={(event) => {
                      setPickedRoom(event.target.value);
                      setNotReadyConfirmed(false);
                    }}
                    className="h-10 rounded-md border border-input bg-background px-3 text-ui"
                  >
                    <option value="">{t('checkIn.pickRoom')}</option>
                    {readyRooms.map((room: Room) => (
                      <option key={room.id} value={room.roomCode}>
                        {room.roomLabel ?? room.roomCode} — {room.roomName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-ui text-muted-foreground">{t('checkIn.noReadyRoom')}</p>
                )}
              </div>
            )}

            <div className="grid gap-1.5">
              <Label htmlFor="checkin-guest">{t('checkIn.guestName')}</Label>
              <Input
                id="checkin-guest"
                placeholder={t('checkIn.guestPlaceholder')}
                value={guestName}
                onChange={(event) => setGuestName(event.target.value)}
                autoFocus
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="checkin-nights">{t('checkIn.nights')}</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="checkin-nights"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MAX_NIGHTS}
                  className="w-20 tnum"
                  value={nights}
                  disabled={customCheckout != null}
                  onChange={(event) => setNights(event.target.value)}
                />
                <span className="text-ui text-muted-foreground" data-testid="checkin-checkout">
                  {customCheckout == null
                    ? t('checkIn.nightsSummary', { count: nightCount, checkout: checkoutText })
                    : t('checkIn.checkoutSummary', { checkout: checkoutText })}
                </span>
              </div>
              {customCheckout == null ? (
                <button
                  type="button"
                  className="w-fit text-caption text-primary underline-offset-2 hover:underline"
                  onClick={() => setCustomCheckout(computedCheckout)}
                >
                  {t('checkIn.changeTime')}
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <DateTimeInput
                    aria-label={t('checkIn.checkoutAria')}
                    value={isoToLocalInput(customCheckout)}
                    onChange={(next) => {
                      const date = new Date(next);
                      if (!Number.isNaN(date.getTime())) setCustomCheckout(date.toISOString());
                    }}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setCustomCheckout(null)}>
                    {t('checkIn.useHouseTime')}
                  </Button>
                </div>
              )}
            </div>

            {canIssueKeys && (
              <div className="grid gap-1.5">
                <Label id="checkin-keys-label">{t('checkIn.keyCount')}</Label>
                <ToggleGroup
                  type="single"
                  className="w-fit"
                  aria-labelledby="checkin-keys-label"
                  value={String(keyCount)}
                  onValueChange={(next) => next && setKeyCount(Number(next))}
                >
                  {KEY_COUNTS.map((count) => (
                    <ToggleGroupItem key={count} value={String(count)} aria-label={t('checkIn.keyCountAria', { count })}>
                      {count}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            )}

            {canIssueKeys && <ReaderStatusLine precondition={precondition} />}

            {roomNotReady && (
              <div className="grid gap-1.5" role="alert">
                <p className="text-ui text-warning-text">{t('checkIn.notReady.warning')}</p>
                <label className="flex items-center gap-2 text-ui">
                  <input
                    type="checkbox"
                    checked={notReadyConfirmed}
                    onChange={(event) => setNotReadyConfirmed(event.target.checked)}
                  />
                  {t('checkIn.notReady.confirm')}
                </label>
              </div>
            )}

            {error && (
              <p role="alert" className="text-ui text-destructive">{error}</p>
            )}
          </div>
        )}

        {step === 'keys' && stayCheckout && (
          <div className="py-2">
            <KeyEncodeStep
              roomCode={targetRoom}
              holderName={guest}
              validUntil={stayCheckout}
              total={keyCount}
              onFinish={finishKeys}
              onBusyChange={setWriting}
            />
          </div>
        )}

        {step === 'done' && (
          <div className="grid gap-2 py-2 text-ui" role="status">
            <p className="text-body font-medium">
              {guest ? t('checkIn.done.guestIn', { guest, room: roomLabel }) : t('checkIn.done.roomIn', { room: roomLabel })}
            </p>
            {canIssueKeys && (
              <p>
                {keys.length === 0
                  ? t('checkIn.done.noKeys')
                  : t('checkIn.done.keys', { count: keys.length })}
              </p>
            )}
            {keys.some((key) => key.outcomeUnknown) && (
              <p className="text-warning-text">{t('checkIn.done.keysPending')}</p>
            )}
            <p className="text-muted-foreground">
              {t('checkIn.done.checkout', { checkout: dayTime(stayCheckout ?? checkout) })}
            </p>
          </div>
        )}

        <DialogFooter>
          {step === 'guest' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t('grms.common.cancel')}
              </Button>
              <Button
                onClick={() => { void confirmGuest(); }}
                disabled={!targetRoom || updateOccupancy.isPending || (roomNotReady && !notReadyConfirmed)}
              >
                {updateOccupancy.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {canIssueKeys ? t('checkIn.continueToKeys', { count: keyCount }) : t('checkIn.checkInOnly')}
              </Button>
            </>
          )}
          {step === 'done' && (
            <Button onClick={() => onOpenChange(false)}>{t('grms.common.done')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
